import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Shield, Award, CheckCircle } from 'lucide-react'

export function CertificationSection() {
  const { t } = useTranslation('common')

  const certifications = [
    {
      icon: Shield,
      title: t('products.certifications.quality.title'),
      description: t('products.certifications.quality.description'),
    },
    {
      icon: Award,
      title: t('products.certifications.safety.title'),
      description: t('products.certifications.safety.description'),
    },
    {
      icon: CheckCircle,
      title: t('products.certifications.traceability.title'),
      description: t('products.certifications.traceability.description'),
    },
  ]

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* BRCGS Badge */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center lg:items-start"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-[#DF631E]/10 blur-3xl rounded-full" />

              {/* Badge container */}
              <div className="relative bg-gradient-to-br from-white to-[#FDF8F3] rounded-2xl p-8 sm:p-10 shadow-xl border border-[#F3E4D6]">
                <img
                  src="/images/certificato.png"
                  alt={t('products.brcgsBadgeAlt')}
                  className="w-full max-w-[280px] h-auto"
                />
              </div>
            </div>

            <p className="mt-6 text-center lg:text-left text-[#4E2118]/70 max-w-sm">
              {t('products.brcgsDescription')}
            </p>
          </motion.div>

          {/* Certification Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#4E2118] mb-4">
              {t('products.certificationsTitle')}
            </h2>
            <p className="text-lg text-[#4E2118]/70 mb-8">
              {t('products.certificationsSubtitle')}
            </p>

            <div className="space-y-6">
              {certifications.map((cert, index) => (
                <motion.div
                  key={cert.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="flex gap-4 p-4 rounded-xl bg-[#FDF8F3] hover:bg-[#F3E4D6]/30 transition-colors duration-300"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#DF631E]/10 flex items-center justify-center">
                    <cert.icon className="w-6 h-6 text-[#DF631E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#4E2118] mb-1">
                      {cert.title}
                    </h3>
                    <p className="text-sm text-[#4E2118]/70">
                      {cert.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
